import { Head, router, useForm } from "@inertiajs/react"
import { Pencil, PlusCircle, Tag, Trash2 } from "lucide-react"
import { useState } from "react"

import Heading from "@/components/heading"
import InputError from "@/components/input-error"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { categoriesPath, categoryPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#f43f5e",
  "#0ea5e9",
  "#8b5cf6",
  "#f97316",
  "#64748b",
]

interface Category {
  id: number
  name: string
  category_type: "expense" | "revenue"
  color: string
}

interface CategoriesProps {
  categories: Category[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Categories", href: categoriesPath() },
]

export default function Categories({ categories }: CategoriesProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)

  const form = useForm({
    name: "",
    category_type: "expense",
    color: COLORS[0],
  })

  function openNew() {
    setEditingCategory(null)
    form.setData({ name: "", category_type: "expense", color: COLORS[0] })
    form.clearErrors()
    setDialogOpen(true)
  }

  function openEdit(category: Category) {
    setEditingCategory(category)
    form.setData({
      name: category.name,
      category_type: category.category_type,
      color: category.color,
    })
    form.clearErrors()
    setDialogOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (editingCategory) {
      form.patch(categoryPath({ id: editingCategory.id }), {
        onSuccess: () => setDialogOpen(false),
      })
    } else {
      form.post(categoriesPath(), {
        onSuccess: () => setDialogOpen(false),
      })
    }
  }

  function handleDelete(category: Category) {
    if (confirm(`Delete "${category.name}"?`)) {
      router.delete(categoryPath({ id: category.id }))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Categories" />

      <div className="mx-auto w-full max-w-2xl space-y-6 p-4">
        <div className="flex items-center justify-between">
          <Heading
            title="Categories"
            description="Organize your entries by category"
          />
          <Button onClick={openNew} size="sm">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        </div>

        {categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
            <Tag className="text-muted-foreground mb-4 h-12 w-12" />
            <h3 className="mb-2 text-lg font-medium">No categories yet</h3>
            <p className="text-muted-foreground mb-6 max-w-sm text-sm">
              Create your first category to start organizing entries. Try
              starting with &ldquo;Software&rdquo;,
              &ldquo;Infrastructure&rdquo;, or &ldquo;Office&rdquo;.
            </p>
            <Button onClick={openNew}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Create Category
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="font-medium">{category.name}</span>
                  <Badge
                    variant="secondary"
                    className={`text-xs ${
                      category.category_type === "expense"
                        ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400"
                        : "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400"
                    }`}
                  >
                    {category.category_type}
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(category)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(category)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Edit Category" : "New Category"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                placeholder="e.g. Software"
                autoFocus
              />
              <InputError messages={form.errors.name} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="category_type">Type</Label>
              <Select
                value={form.data.category_type}
                onValueChange={(value) => form.setData("category_type", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="revenue">Revenue</SelectItem>
                </SelectContent>
              </Select>
              <InputError messages={form.errors.category_type} />
            </div>

            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 transition-transform ${
                      form.data.color === color
                        ? "border-foreground scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => form.setData("color", color)}
                  />
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.processing}>
                {editingCategory ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
